import React, { useState, useEffect, useRef } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Spinner, Badge } from "reactstrap";
import { get, post } from "helpers/api_helper";
import Toaster from "components/toster";
import moment from "moment";

const MapTrayModal = ({ isOpen = false, caseNo = "", orderNumber = "", onClose = () => { }, onSuccess = () => { } }) => {
    const [tryNo, setTryNo] = useState("");
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [isMapped, setIsMapped] = useState(false);
    const [existingData, setExistingData] = useState(null);
    const inputRef = useRef(null);

    const activeCaseNo = caseNo || orderNumber || "";

    useEffect(() => {
        if (isOpen && activeCaseNo) {
            setTryNo("");
            setIsMapped(false);
            setExistingData(null);
            fetchCurrentMapping();
        }
    }, [isOpen, activeCaseNo]);

    const fetchCurrentMapping = async () => {
        setFetching(true);
        try {
            const res = await get(`/api/tryprocess/get_case_tray?case_no=${encodeURIComponent(activeCaseNo)}`);
            if (res && res.is_mapped && res.data) {
                setIsMapped(true);
                setExistingData(res.data);
                setTryNo(res.data.try_no || "");
            } else {
                setIsMapped(false);
                setExistingData(null);
                setTryNo("");
            }
        } catch (err) {
            console.error("Failed to fetch tray mapping:", err);
        } finally {
            setFetching(false);
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                    if (inputRef.current.select) {
                        inputRef.current.select();
                    }
                }
            }, 100);
        }
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        const trimmedTry = (tryNo || "").trim();
            const trimmedCase = (activeCaseNo || "").trim();

            if (!trimmedCase || trimmedCase.length < 4) {
                Toaster("error", { title: "Validation", message: "Case / Order number must be at least 4 characters." });
                return;
            }

                if (!trimmedTry || trimmedTry.length < 4) {
                    Toaster("warning", { title: "Validation", message: "Tray Number must be at least 4 digits or characters (e.g. 1234)." });
                    return;
                }

                setLoading(true);
                try {
                    const res = await post("/api/tryprocess/maptry", {
                        case_no: activeCaseNo,
                        try_no: trimmedTry
                    });

                    if (res && res.status) {
                        const msg = res.msg || (isMapped ? `Tray mapping updated to ${trimmedTry}` : `Case mapped to tray ${trimmedTry}`);
                        Toaster("success", { title: isMapped ? "Tray Updated" : "Tray Mapped", message: msg });
                        onSuccess();
                        onClose();
                    } else {
                        Toaster("error", { title: "Mapping Failed", message: (res && res.msg) ? res.msg : "Failed to map tray." });
                    }
                } catch (err) {
                    Toaster("error", { title: "Error", message: err?.message || "An error occurred while mapping tray." });
                } finally {
                    setLoading(false);
                }
            };

            return (
                <Modal isOpen={isOpen} toggle={onClose} centered size="md">
                    <ModalHeader toggle={onClose}>
                        <i className="bx bx-shield-quarter me-2 text-primary font-size-18"></i>
                        {isMapped ? "Edit / Update Tray Mapping" : "Assign / Map Tray"}
                    </ModalHeader>
                    <Form onSubmit={handleSubmit}>
                        <ModalBody>
                            {fetching ? (
                                <div className="text-center py-4">
                                    <Spinner size="sm" color="primary" className="me-2" />
                                    <span className="text-muted">Checking current tray mapping...</span>
                                </div>
                            ) : (
                                <>
                                    <FormGroup className="mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <Label className="fw-semibold mb-0">Case / Model #</Label>
                                            {isMapped ? (
                                                <Badge color="success" pill>
                                                    Currently Mapped
                                                </Badge>
                                            ) : (
                                                <Badge color="secondary" pill>
                                                    Not Mapped
                                                </Badge>
                                            )}
                                        </div>
                                        <Input
                                            type="text"
                                            value={activeCaseNo}
                                            readOnly
                                            disabled
                                            className="bg-light fw-bold text-dark font-size-15"
                                        />
                                        {orderNumber && orderNumber !== caseNo && (
                                            <small className="text-muted d-block mt-1">
                                                Order #: <span className="fw-semibold">{orderNumber}</span>
                                            </small>
                                        )}
                                        {isMapped && existingData?.datetime && (
                                            <small className="text-muted d-block mt-1">
                                                Last mapped: <span className="fw-semibold text-primary">{moment(existingData.datetime).format("DD-MM-YYYY HH:mm:ss")}</span>
                                            </small>
                                        )}
                                    </FormGroup>

                                    <FormGroup className="mb-2">
                                        <Label className="fw-semibold" for="trayNumberInput">
                                            Tray Number <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            innerRef={inputRef}
                                            id="trayNumberInput"
                                            type="text"
                                            className="form-control font-size-15 fw-bold"
                                            placeholder="Scan barcode or enter Tray No (e.g. 0044)"
                                            value={tryNo}
                                            onChange={(e) => setTryNo(e.target.value)}
                                            disabled={loading}
                                            autoComplete="off"
                                        />
                                        <small className="text-muted mt-1 d-block">
                                            {isMapped
                                                ? "Change the Tray Number above to re-assign or update this case."
                                                : "Scan the physical tray barcode or type the tray number and press Enter."}
                                        </small>
                                    </FormGroup>
                                </>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button color="secondary" type="button" onClick={onClose} disabled={loading}>
                                Cancel
                            </Button>
                            <Button color={isMapped ? "success" : "primary"} type="submit" disabled={loading || fetching || !tryNo.trim()}>
                                {loading ? (
                                    <>
                                        <Spinner size="sm" className="me-1" /> Saving...
                                    </>
                                ) : isMapped ? (
                                    <>
                                        <i className="bx bx-check-double me-1"></i> Update Tray Mapping
                                    </>
                                ) : (
                                    <>
                                        <i className="bx bx-check me-1"></i> Assign to Tray
                                    </>
                                )}
                            </Button>
                        </ModalFooter>
                    </Form>
                </Modal>
            );
        };

        export default MapTrayModal;
